import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  Shield, 
  LogOut, 
  Loader2,
  Settings,
  DollarSign,
  Save,
  RotateCcw,
  Download,
  FileDown,
  Users,
  MessageSquare,
  Target,
  CreditCard,
  FileText
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";

const API = "/api";

const ExportCard = ({ title, description, icon: Icon, onExport, loading, color }) => (
  <div className="bg-[#0a0a0a] border border-[#222] rounded-lg p-4 flex items-center justify-between">
    <div className="flex items-center gap-4">
      <div className={`p-3 rounded-lg ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <h4 className="text-white font-medium">{title}</h4>
        <p className="text-[#666] text-sm">{description}</p>
      </div>
    </div>
    <Button 
      onClick={onExport}
      disabled={loading}
      variant="outline"
      className="border-[#333] text-[#888] hover:text-white hover:bg-[#222]"
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
      {loading ? "Exporting..." : "Export CSV"}
    </Button>
  </div>
);

export default function AdminSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [exportLoading, setExportLoading] = useState({});
  const adminEmail = localStorage.getItem("admin_email");

  // Form state
  const [formData, setFormData] = useState({
    free_conversations: 3,
    free_file_limit_mb: 5,
    pro_price: 29,
    pro_file_limit_mb: 25,
    plus_price_per_conversation: 1,
    plus_min_conversations: 10,
    plus_max_conversations: 50
  });

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/login");
      return;
    }
    fetchSettings();
  }, [navigate]);

  const fetchSettings = async () => {
    setLoading(true);
    const token = localStorage.getItem("admin_token");
    
    try {
      const response = await fetch(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/login");
        return;
      }
      
      const data = await response.json();
      setSettings(data);
      
      // Populate form
      if (data.free_plan) {
        setFormData(prev => ({
          ...prev,
          free_conversations: data.free_plan.conversations_per_month || 3,
          free_file_limit_mb: data.free_plan.file_upload_limit_mb || 5
        }));
      }
      if (data.pro_plan) {
        setFormData(prev => ({
          ...prev,
          pro_price: data.pro_plan.price || 29,
          pro_file_limit_mb: data.pro_plan.file_upload_limit_mb || 25
        }));
      }
      if (data.plus_plan) {
        setFormData(prev => ({
          ...prev,
          plus_price_per_conversation: data.plus_plan.price_per_conversation || 1,
          plus_min_conversations: data.plus_plan.min_conversations || 10,
          plus_max_conversations: data.plus_plan.max_conversations || 50
        }));
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem("admin_token");
    
    try {
      const response = await fetch(`${API}/admin/settings`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        throw new Error("Failed to save settings");
      }
      
      toast.success("Settings saved successfully");
      fetchSettings();
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async (type) => {
    setExportLoading(prev => ({ ...prev, [type]: true }));
    const token = localStorage.getItem("admin_token");
    
    try {
      const response = await fetch(`${API}/admin/export/${type}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.csv && data.count > 0) {
        // Create and download the file
        const blob = new Blob([data.csv], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = data.filename || `${type}_export.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        toast.success(`Exported ${data.count} ${type} records`);
      } else {
        toast.info(`No ${type} data to export`);
      }
    } catch (error) {
      toast.error(`Failed to export ${type}`);
    } finally {
      setExportLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_email");
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">System Settings</h1>
          <p className="text-[#666]">Manage pricing plans and export data</p>
        </div>

        {/* Pricing Settings */}
        <div className="bg-[#111] border border-[#222] rounded-xl p-6 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-[#14A800]/20 rounded-lg">
              <DollarSign className="w-5 h-5 text-[#14A800]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Pricing Configuration</h2>
              <p className="text-[#666] text-sm">Configure plan limits and pricing</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Free Plan */}
            <div className="bg-[#0a0a0a] rounded-lg p-4">
              <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                <span className="w-2 h-2 bg-gray-500 rounded-full"></span>
                Free Plan
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="free_conversations" className="text-[#888] text-sm">Conversations per Month</Label>
                  <Input
                    id="free_conversations"
                    type="number"
                    min="1"
                    max="100"
                    value={formData.free_conversations}
                    onChange={(e) => setFormData(prev => ({ ...prev, free_conversations: parseInt(e.target.value) || 0 }))}
                    className="bg-[#111] border-[#333] text-white mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="free_file_limit" className="text-[#888] text-sm">File Upload Limit (MB)</Label>
                  <Input
                    id="free_file_limit"
                    type="number"
                    min="1"
                    max="100"
                    value={formData.free_file_limit_mb}
                    onChange={(e) => setFormData(prev => ({ ...prev, free_file_limit_mb: parseInt(e.target.value) || 0 }))}
                    className="bg-[#111] border-[#333] text-white mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Pro Plan */}
            <div className="bg-[#0a0a0a] rounded-lg p-4">
              <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                Pro Plan
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="pro_price" className="text-[#888] text-sm">Monthly Price ($)</Label>
                  <Input
                    id="pro_price"
                    type="number"
                    min="1"
                    max="500"
                    value={formData.pro_price}
                    onChange={(e) => setFormData(prev => ({ ...prev, pro_price: parseInt(e.target.value) || 0 }))}
                    className="bg-[#111] border-[#333] text-white mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="pro_file_limit" className="text-[#888] text-sm">File Upload Limit (MB)</Label>
                  <Input
                    id="pro_file_limit"
                    type="number"
                    min="1"
                    max="500"
                    value={formData.pro_file_limit_mb}
                    onChange={(e) => setFormData(prev => ({ ...prev, pro_file_limit_mb: parseInt(e.target.value) || 0 }))}
                    className="bg-[#111] border-[#333] text-white mt-1"
                  />
                </div>
              </div>
              <p className="text-[#555] text-xs mt-2">Pro plan includes unlimited conversations</p>
            </div>

            {/* Plus Plan */}
            <div className="bg-[#0a0a0a] rounded-lg p-4">
              <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                <span className="w-2 h-2 bg-[#14A800] rounded-full"></span>
                Plus Credits (À La Carte)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="plus_price" className="text-[#888] text-sm">Price per Conversation ($)</Label>
                  <Input
                    id="plus_price"
                    type="number"
                    min="0.1"
                    max="10"
                    step="0.1"
                    value={formData.plus_price_per_conversation}
                    onChange={(e) => setFormData(prev => ({ ...prev, plus_price_per_conversation: parseFloat(e.target.value) || 0 }))}
                    className="bg-[#111] border-[#333] text-white mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="plus_min" className="text-[#888] text-sm">Minimum Purchase</Label>
                  <Input
                    id="plus_min"
                    type="number"
                    min="1"
                    max="100"
                    value={formData.plus_min_conversations}
                    onChange={(e) => setFormData(prev => ({ ...prev, plus_min_conversations: parseInt(e.target.value) || 0 }))}
                    className="bg-[#111] border-[#333] text-white mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="plus_max" className="text-[#888] text-sm">Maximum Purchase</Label>
                  <Input
                    id="plus_max"
                    type="number"
                    min="1"
                    max="500"
                    value={formData.plus_max_conversations}
                    onChange={(e) => setFormData(prev => ({ ...prev, plus_max_conversations: parseInt(e.target.value) || 0 }))}
                    className="bg-[#111] border-[#333] text-white mt-1"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-[#222]">
            <Button
              onClick={fetchSettings}
              variant="ghost"
              className="text-[#888] hover:text-white"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#14A800] hover:bg-[#128a00] text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>

          {settings?.updated_at && (
            <p className="text-[#555] text-xs mt-4">
              Last updated: {new Date(settings.updated_at).toLocaleString()} by {settings.updated_by}
            </p>
          )}
        </div>

        {/* Data Export */}
        <div className="bg-[#111] border border-[#222] rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <FileDown className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Data Export</h2>
              <p className="text-[#666] text-sm">Export platform data as CSV files</p>
            </div>
          </div>

          <div className="space-y-3">
            <ExportCard
              title="Users"
              description="Export all user accounts and profiles"
              icon={Users}
              color="bg-blue-600"
              onExport={() => handleExport("users")}
              loading={exportLoading.users}
            />
            <ExportCard
              title="Conversations"
              description="Export all conversations with message counts"
              icon={MessageSquare}
              color="bg-green-600"
              onExport={() => handleExport("conversations")}
              loading={exportLoading.conversations}
            />
            <ExportCard
              title="Leads"
              description="Export all captured leads and contact info"
              icon={Target}
              color="bg-purple-600"
              onExport={() => handleExport("leads")}
              loading={exportLoading.leads}
            />
            <ExportCard
              title="Payments"
              description="Export all payment transactions"
              icon={CreditCard}
              color="bg-yellow-600"
              onExport={() => handleExport("payments")}
              loading={exportLoading.payments}
            />
            <ExportCard
              title="Files"
              description="Export uploaded file metadata"
              icon={FileText}
              color="bg-orange-600"
              onExport={() => handleExport("files")}
              loading={exportLoading.files}
            />
          </div>
        </div>
      </main>
    </div>
  );
}
